import {
  Control,
  useForm,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useCallback, useState } from "react";
import { useOutletContext } from "react-router";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  BBEOutletContext,
  BrokerSummary,
  BrokerSummarySavePayload,
  CarrierRuleSelection,
} from "../pages/BBE/types";
import { extractErrorMessage } from "../utils";
import { CARRIER_BROKER_SETTING_FORM_SCHEMA } from "../pages/BBE/constants";

import useToastNotificationService from "./useToastNotificationService";
import { useSaveBrokerSummaryMutation } from "./useBbe";

type BrokerSettingFormValues = z.infer<
  typeof CARRIER_BROKER_SETTING_FORM_SCHEMA
>;

type Args = {
  brokerSummary: BrokerSummary;
  refetch: () => void;
};

type UseBrokerSettingFormResult = {
  control: Control<BrokerSettingFormValues>;
  watch: UseFormWatch<BrokerSettingFormValues>;
  setBrokerSummaryId: (value: string) => void;
  isPending: boolean;
  setCarrierRule: (value: CarrierRuleSelection) => void;
  handleSave: () => Promise<void>;
  carrierRule: CarrierRuleSelection;
  isValid: boolean;
  isDirty: boolean;
  setValue: UseFormSetValue<BrokerSettingFormValues>;
};

export function useBrokerSettingForm({
  brokerSummary,
  refetch,
}: Args): UseBrokerSettingFormResult {
  const { createToastNotification } = useToastNotificationService();
  const { brokerId: sfBrokerId } = useOutletContext<BBEOutletContext>();

  const [brokerSummaryId, setBrokerSummaryId] = useState<string>(
    brokerSummary.id,
  );
  const [carrierRule, setCarrierRule] = useState<CarrierRuleSelection | null>(
    null,
  );

  const defaultFormValues = {
    paidBonus: brokerSummary.previousYearBonus ?? 0,
    bonusGoal: brokerSummary.bonusGoal ?? 0,
    locCount: brokerSummary.beginningLoc ?? 0,
    beginningBookSize: brokerSummary.beginningBookSize ?? 0,
    carrierRuleId: brokerSummary.carrierRuleId,
  };

  const {
    control,
    watch,
    formState: { isValid, isDirty },
    setValue,
    reset,
  } = useForm<BrokerSettingFormValues>({
    resolver: zodResolver(CARRIER_BROKER_SETTING_FORM_SCHEMA),
    mode: "all",
    defaultValues: defaultFormValues,
  });

  const beginningBookSize = watch("beginningBookSize");
  const locCount = watch("locCount");
  const bonusGoal = watch("bonusGoal");
  const paidBonus = watch("paidBonus");

  const { mutateAsync, isPending } = useSaveBrokerSummaryMutation(
    sfBrokerId,
    brokerSummaryId,
  );

  const handleSave = useCallback(async (): Promise<void> => {
    try {
      const parsed = CARRIER_BROKER_SETTING_FORM_SCHEMA.parse({
        beginningBookSize,
        locCount,
        paidBonus,
        bonusGoal,
        carrierRuleId: carrierRule.carrierRuleId,
      });

      const payload: BrokerSummarySavePayload = {
        sfCarrierId: carrierRule.carrierId,
        beginningBookSize: parsed.beginningBookSize,
        beginningLoc: parsed.locCount,
        previousYearBonus: parsed.paidBonus,
        bonusGoal: parsed.bonusGoal,
        carrierRuleId: carrierRule.carrierRuleId || undefined,
        trancheCode: carrierRule.ruleId || undefined,
        isCurrent: true,
      };
      await mutateAsync(payload);
      createToastNotification({
        message: `${carrierRule?.ruleTitle} successfully updated`,
        severity: "success",
      });
      void refetch();
      reset({
        beginningBookSize: parsed.beginningBookSize,
        locCount: parsed.locCount,
        paidBonus: parsed.paidBonus,
        bonusGoal: parsed.bonusGoal,
        carrierRuleId: parsed.carrierRuleId,
      });
    } catch (error) {
      createToastNotification({
        title: `Failed to save broker summary for ${carrierRule?.ruleTitle}`,
        severity: "error",
        message: extractErrorMessage(
          error,
          `Error updating ${carrierRule?.ruleTitle}`,
        ),
      });
      throw error;
    }
  }, [
    beginningBookSize,
    bonusGoal,
    carrierRule?.carrierId,
    carrierRule?.carrierRuleId,
    carrierRule?.ruleId,
    carrierRule?.ruleTitle,
    createToastNotification,
    locCount,
    mutateAsync,
    paidBonus,
    refetch,
    reset,
  ]);

  return {
    control,
    watch,
    setBrokerSummaryId,
    isPending,
    setCarrierRule,
    handleSave,
    carrierRule,
    isValid,
    isDirty,
    setValue,
  };
}
